import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { GarnishmentProcessingType } from '@safarilaw-webapp/feature/co-manage/data-access';
import { GarnishmentAccount, GarnishmentLedgerTransactionType, Transfer } from '@safarilaw-webapp/feature/lpms/data-access';
import { SafariObject } from '@safarilaw-webapp/shared/common-objects-models';

import { CurrencyPipe } from '@angular/common';
import { FileManagementComponentService } from '@safarilaw-webapp/shared/ui-kit/filemanagementcomponentservice';
import { FormSubmitInfo, jsonTypeToFormGroup, SafariFormComponent, SafariValidators } from '@safarilaw-webapp/shared/ui-kit/safari-form';
import { NgxCurrencyDirective } from 'ngx-currency';
import { combineLatest, filter } from 'rxjs';
import { ReduxFormId } from '../../../../../../../enums';
import { GarnishmentFinancialsInfo } from '../../../matter-edit-form/mixins/garnishment-financials';

@Component({
  selector: 'sl-lpms-document-transfer-new-dialog-form',
  templateUrl: './lpms-document-transfer-new-dialog-form.component.html',
  styleUrls: ['./lpms-document-transfer-new-dialog-form.scss'],
  providers: [FileManagementComponentService],
  imports: [SafariFormComponent.standaloneImports, CurrencyPipe, NgxCurrencyDirective]
})
export class DocumentTransferNewDialogFormComponent extends SafariFormComponent<Transfer, jsonTypeToFormGroup<Transfer>> implements OnInit {
  @Input() accounts: GarnishmentAccount[];
  @Input() totalProcessingFeeInNewAccounts: number;
  @Input() pendingLevyAmount: number;
  @Input() totalSeizedAmountInNewAccounts: number;

  @Input() garnishmentProcessingType: GarnishmentProcessingType;
  @Input() garnishmentFinancialsInfo: GarnishmentFinancialsInfo;
  @Input() minimalThresholdAmount: number;
  private _currencyGreaterThanZeroValidator = null;
  private _currencyNotEqualZeroValidator = null;
  private _sumOfProcessingFeesValidator = null;
  private _cantSeizeMoreThenPendingValidator = null;
  private _cantReleaseMoreThenSeizedValidator = null;
  private _cantChargeProcessingFeeHigherThanMaxProcessingFee = null;
  constructor() {
    super();
    this.componentId = ReduxFormId.DocumentTransferNewDialogEdit;
  }
  get isSeizingFunds(): boolean {
    return this.appModel.type == GarnishmentLedgerTransactionType.SeizeFunds;
  }
  get isReleasingFunds(): boolean {
    return this.appModel.type == GarnishmentLedgerTransactionType.ReleaseFunds;
  }
  get isSettingProcessingFee(): boolean {
    return this.appModel.type == GarnishmentLedgerTransactionType.ProcessingFee;
  }
  get shouldShowMaxSeizableAmountLabel() {
    return this.isSeizingFunds;
  }
  get shouldShowMaxReleasableAmountLabel() {
    return this.isReleasingFunds && !String.isNullOrEmpty(this.form.controls.toAccountNumber.value);
  }
  isToAccountPicklist() {
    return this.isReleasingFunds;
  }
  isFromAccountPicklist() {
    return !this.isReleasingFunds;
  }
  get hasMinimalThresholdAmount(): boolean {
    return !!this.minimalThresholdAmount;
  }
  get shouldShowMinimalThresholdAmount(): boolean {
    return this.hasMinimalThresholdAmount && this.isSeizingFunds;
  }
  get requiredMinimumText(): string {
    return `Required Minimum: $${this.minimalThresholdAmount.toFixed(2)}`;
  }
  get maxReleasableAmount(): number {
    if (!this.shouldShowMaxReleasableAmountLabel) {
      return null;
    }

    return this.garnishmentFinancialsInfo?.maxReleasableAmountForAccount.get(this.form.controls.toAccountNumber.value) ?? 0;
  }

  ngOnInit() {
    super.ngOnInit();
  }

  override createValueChangeSubscription() {
    super.createValueChangeSubscription();
    this._currencyNotEqualZeroValidator = SafariValidators.currencyNotEqualValue(0, 'Value can not be zero.');
    this._currencyGreaterThanZeroValidator = SafariValidators.currencyGreaterThanZero('Must be greater than zero.');
    // If we're not using automated transfer mode then we need to validate that amount is greater than zero in all cases.
    // If on the other hand we're in automated transfer mode we will have special handling for processing fee
    if (this.appModel.type != GarnishmentLedgerTransactionType.ProcessingFee || this.garnishmentProcessingType != GarnishmentProcessingType.Ledger_Automated) {
      this.addValidators(this.form.controls.amount, [Validators.required, this._currencyGreaterThanZeroValidator]);
    } else {
      // If we get there we're in processingfee transaction type and automated global transfer mode.
      // We need to check every change of "enableAutomatedTransfer"  on the transaction
      this.subscribe(
        combineLatest([this.valueChanges(this.form.controls.enableAutomatedTransfer), this.valueChanges(this.form.controls.fromAccountNumber)]).pipe(filter(x => x != null)),
        ([enableAutomatedTransfer, fromAccountNumber]) => {
          this.clearValidators(this.form.controls.amount);
          // If manual then we allow less than 0, but the sum of all processing fees must be greater than zero
          if (enableAutomatedTransfer == false) {
            const allProcessingFees = this.garnishmentFinancialsInfo?.totalProcessingFeeAmountForAccount.get(fromAccountNumber) || 0;

            this._sumOfProcessingFeesValidator = SafariValidators.currencyGreaterThanZero('Sum of processing fees cannot be less than zero.', allProcessingFees + 0.001);
            this.addValidators(this.form.controls.amount, [Validators.required, this._currencyNotEqualZeroValidator, this._sumOfProcessingFeesValidator]);
          } else {
            this.addValidators(this.form.controls.amount, [Validators.required, this._currencyGreaterThanZeroValidator]);
          }
          if (this._cantChargeProcessingFeeHigherThanMaxProcessingFee) {
            this.addValidators(this.form.controls.amount, [this._cantChargeProcessingFeeHigherThanMaxProcessingFee]);
          }
        }
      );
    }
    // Disable "enableAutomatedTransfer" by default. It will be enabled/disabled by account changes later on
    this.disable(this.form.controls.enableAutomatedTransfer);

    // Set up default values based on incoming transaction ledger type (does not change during duration of the dialog)
    if (this.isSeizingFunds) {
      const originalAmount = this.appModel.amount; // either 0 or something if editing an existing one

      // In seizeFunds mode only fromAccountNumber is enabled. toAccount can not be edited and will go to whatever is defined in company setup
      this.enable(this.form.controls.fromAccountNumber);
      this.addValidators(this.form.controls.fromAccountNumber, [Validators.required]);

      this.disable(this.form.controls.toAccountNumber);

      this._cantSeizeMoreThenPendingValidator = SafariValidators.currencyLessThanOrEqualValue(
        this.garnishmentFinancialsInfo?.maxSeizableAmount + originalAmount + 0.001,
        'Cannot seize more than pending levy amount and what was already seized.'
      );
      this.addValidators(this.form.controls.amount, [Validators.required, this._cantSeizeMoreThenPendingValidator]);

      this.subscribe(this.valueChanges(this.form.controls.fromAccountNumber), x => {
        // If there is only one account (on the left side) and we haven't touched this control
        // then default to max seizable amount.
        if (this.accounts?.length == 1 && !this.form.controls.amount.touched) {
          this.setValue(this.form.controls.amount, this.garnishmentFinancialsInfo?.maxSeizableAmount);
        }
      });
      // Now set up monitoring for autotransfer toggle depending on the transactiontype
      this._monitorAccountChangesForAutoTransferToggle(this.form.controls.fromAccountNumber);
      // If there's only one account then we can set it as default
      if (this.accounts.length == 1) {
        // If only one account default to that one, but do that in a timeout so that every other subscription
        // will apply by this time
        setTimeout(() => {
          this.setValue(this.form.controls.fromAccountNumber, this.accounts[0].number);
        });
      }
    } else if (this.isReleasingFunds) {
      const originalAcctNumber = this.appModel.toAccountNumber; // eitehr undefined or something if editing an existing one
      const originalAmount = this.appModel.amount; // either 0 or something if editing an existing one

      // In releasefunds mode toAccountNumber is enabled. fromAccountNumber can not be edited and will go to whatever is defined in company setup
      this.enable(this.form.controls.toAccountNumber);
      this.addValidators(this.form.controls.toAccountNumber, [Validators.required]);
      this.disable(this.form.controls.fromAccountNumber);

      this.subscribe(this.valueChanges(this.form.controls.toAccountNumber).pipe(filter(x => x != null)), x => {
        // Every time we change account number we need to recalculate total released and seized for the account.
        // We'll validate on this so that we can't release more than was seized in the account
        const totalSeizedForTheAccount = this.garnishmentFinancialsInfo?.totalSeizedFundsAmountForAccount.get(x) || 0;
        const totalReleasedForTheAccount = this.garnishmentFinancialsInfo?.totalReleasedAmountForAccount.get(x) || 0;

        // Using same trick as isCurrentlySeizedMoreThanPendingLien to prevent rounding issues
        let maxValue = totalSeizedForTheAccount - totalReleasedForTheAccount + 0.001;
        if (x == originalAcctNumber) {
          // If we're changing back to the original account then we need to add the original amount to the max value
          // to prevent validation error
          maxValue += originalAmount;
        }
        this.clearValidators(this.form.controls.amount);
        this._cantReleaseMoreThenSeizedValidator = SafariValidators.currencyLessThanOrEqualValue(maxValue, 'Release amount cannot exceed seized amount for selected account');
        this.addValidators(this.form.controls.amount, [this._currencyGreaterThanZeroValidator, Validators.required, this._cantReleaseMoreThenSeizedValidator]);
      });
      // Now set up monitoring for autotransfer toggle depending on the transactiontype
      this._monitorAccountChangesForAutoTransferToggle(this.form.controls.toAccountNumber);

      if (this.accounts?.length == 1) {
        this.setValue(this.form.controls.toAccountNumber, this.accounts[0].number);
      }
    } else if (this.isSettingProcessingFee) {
      const originalAmount = this.appModel.amount; // either 0 or something if editing an existing one
      this.enable(this.form.controls.fromAccountNumber);
      this.disable(this.form.controls.toAccountNumber);

      if (this.garnishmentFinancialsInfo?.remainingAllowedProcessingFeeAmount != null) {
        this._cantChargeProcessingFeeHigherThanMaxProcessingFee = SafariValidators.currencyLessThanOrEqualValue(
          this.garnishmentFinancialsInfo?.remainingAllowedProcessingFeeAmount + originalAmount + 0.001,
          this.garnishmentFinancialsInfo?.maxProcessingFeeExceededMessage
        );
        this.addValidators(this.form.controls.amount, this._cantChargeProcessingFeeHigherThanMaxProcessingFee);
      }
      this.addValidators(this.form.controls.amount, Validators.required);

      this.addValidators(this.form.controls.fromAccountNumber, [Validators.required]);
      if (this.accounts?.length == 1) {
        this.setValue(this.form.controls.fromAccountNumber, this.accounts[0].number);
      }
      // Now set up monitoring for autotransfer toggle depending on the transactiontype
      this._monitorAccountChangesForAutoTransferToggle(this.form.controls.fromAccountNumber);
    }
  }
  onModelBound() {
    super.createForm({
      id: [undefined],
      amount: [undefined, Validators.required],
      fromAccountNumber: [undefined],
      toAccountNumber: [undefined],
      enableAutomatedTransfer: [undefined],
      type: [undefined]
    });

    super.patchForm({
      id: this.appModel.id,
      amount: this.appModel.amount,
      fromAccountNumber: this.appModel.fromAccountNumber,
      toAccountNumber: this.appModel.toAccountNumber,
      type: this.appModel.type
      // Don't set automated transfer to anything. It has defaults based on the selected account type
    });
  }

  private _monitorAccountChangesForAutoTransferToggle(control: FormControl<any>) {
    // This only applies for automated ledger, so if not automated skip
    if (this.garnishmentProcessingType == GarnishmentProcessingType.Ledger_Automated) {
      // We need to listen to changes in from/to account (based on the type)
      this.subscribe(this.valueChanges(control), value => {
        // Every time a new account is selected we need to find out if it has automated transfer enabled
        // for the account in general.
        // If so we'll default transaction transfer to True and allow it to be edited.
        // If not we'll disable it and default to False
        if (value) {
          const acct = this.accounts.find(x => SafariObject.idEqual(x.number, value));
          if (acct?.enableAutomatedTransfer) {
            this.setValue(this.form.controls.enableAutomatedTransfer, true);
            this.enable(this.form.controls.enableAutomatedTransfer);
          } else {
            this.setValue(this.form.controls.enableAutomatedTransfer, false);
            this.disable(this.form.controls.enableAutomatedTransfer);
          }
        } else {
          this.disable(this.form.controls.enableAutomatedTransfer);
        }
      });
    }
  }

  onSubmit(submitInfo: FormSubmitInfo<Transfer>) {
    submitInfo.appModel = { ...submitInfo.appModel, ...submitInfo.formModel };
    if (this.garnishmentProcessingType == GarnishmentProcessingType.Ledger_Manual) {
      submitInfo.appModel.enableAutomatedTransfer = false;
    }
    return super.onSubmit(submitInfo);
  }
  get shouldShowEnableAutomatedTransfer() {
    return this.garnishmentProcessingType == GarnishmentProcessingType.Ledger_Automated;
  }
}
